import { ProjectBranchConfigResponse } from '@/app/models/projectBranchConfig';
import { useDeleteProjectBranchConfigMutation } from '@/app/services/projectBranchConfigApi';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ModeEditOutlineTwoToneIcon from '@mui/icons-material/ModeEditOutlineTwoTone';
import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ActionMenu, { ItemAction } from '../common/ActionMenu';

const HeaderTableCell = styled(TableCell)(() => ({
  fontSize: 13,
  justifyContent: 'space-between',
  fontWeight: 'bold',
}));

const RowTableCell = styled(TableCell)(() => ({
  fontSize: 13,
  justifyContent: 'space-between',
}));

type BranchResultCenterTableParams = {
  rows: ProjectBranchConfigResponse[];
  count: number;
  rowsPerPage: number;
  setRowsPerPage: (n: number) => void;
  page: number;
  setPage: (n: number) => void;
  setSelectedItem: (p: ProjectBranchConfigResponse) => void;
  setDrawerStatus: (p: boolean) => void;
};

export default function BranchResultCenterTable(props: BranchResultCenterTableParams) {
  const { t } = useTranslation(['branchResultCenter', 'common']);
  const {
    rows,
    count,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    setSelectedItem,
    setDrawerStatus,
  } = props;

  const [deleteProjectBranchCOnfig] = useDeleteProjectBranchConfigMutation();

  const handleDelete = async (id: number) => {
    const response = await deleteProjectBranchCOnfig(id);
    if ('data' in response) {
      toast.success(
        t('deleted.project.branch.config', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <TableContainer sx={{ marginLeft: 0, padding: 0, width: '100%' }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <HeaderTableCell colSpan={4}>{t('branch')}</HeaderTableCell>
            <HeaderTableCell colSpan={6}>{t('result.center')}</HeaderTableCell>
            <HeaderTableCell colSpan={4} align="right">
              {t('common:actions')}
            </HeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <RowTableCell colSpan={4}>{`${row.branch.code} - ${row.branch.name}`}</RowTableCell>
              <RowTableCell colSpan={6}>{`${row.project.code} - ${row.project.name}`}</RowTableCell>
              <RowTableCell colSpan={4} align="right">
                <ActionMenu>
                  <ItemAction
                    text={t('common:delete')}
                    Icon={DeleteOutlineIcon}
                    onClick={() => {
                      handleDelete(row.id);
                    }}
                  />
                  <ItemAction
                    text={t('common:edit')}
                    Icon={ModeEditOutlineTwoToneIcon}
                    onClick={() => {
                      setSelectedItem(row);
                      setDrawerStatus(true);
                    }}
                  />
                </ActionMenu>
              </RowTableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell sx={{ textAlign: 'center' }} colSpan={14}>
                <Typography variant="subtitle2">
                  {t('common:no.data.for.the.chosen.period.and.search.term')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        sx={{ width: '100%' }}
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelDisplayedRows={({ from, to, count: tcount }) => `${from}-${to} ${t('de')} ${tcount}`}
        labelRowsPerPage={t('common:items.per.page')}
      />
    </TableContainer>
  );
}
